import { Icon } from 'Components';
import { Select, Table, Spin } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { PropertyData, SelectOption, TABLE_STATE, UserData } from '../CampaignReport.type';
import UserDetailDrawer from './UserDetailDrawer';
import { useIsMobile } from 'Hooks';
import PaginationElement from 'PhishingManager/components/Pagination';
import { INDIVIDUAL_TABLE_COLUMNS, PROPERTY_TABLE_COLUMNS } from '../CampaignReport.constant';
import PropertyCard from './PropertyCard';
import { ColumnsType } from 'antd/es/table';
import IndividualListItem from './IndividualListItem';
import PropertyListItem from './PropertyListItem';
import DebounceInput from 'PhishingManager/components/DebouncedInput';
import InfinityScrollWrapper from 'Components/InfinityScroll/InfinityScrollWrapper';
import { capitalize } from 'PhishingManager/utils';

// Performed By Options
const performedByOptions: SelectOption[] = [
    { label: "All Sources", value: "" },
    { label: "Human", value: "Human" },
    { label: "System", value: "System" },
    { label: "Unknown", value: "Unknown" },
];

// Result Options
const resultOptions: SelectOption[] = [
    { label: "All Results", value: "" },
    { label: "Passed", value: "Passed" },
    { label: "Failed", value: "Failed" }
];

// Interaction Options
const interactionOptions: SelectOption[] = [
    { label: "All Interactions", value: "" },
    { label: "No Interaction", value: "No Interaction" },
    { label: "Email Sent", value: "Email Sent" },
    { label: "Email Opened", value: "Email Opened" },
    { label: "Clicked Link", value: "Clicked Link" },
    { label: "Submitted Data", value: "Submitted Data" }
];

// Interaction Options
const DELIVERY_STATUS: SelectOption[] = [
    { label: "All delivery statuses", value: "" },
    { label: "Delivered", value: "Yes" },
    { label: "Undelivered", value: 'No' }
];

const getUniqueFormattedValues = (data: UserData[], key: keyof UserData) => {
    const values: SelectOption[] = [{ label: `All ${capitalize(String(key))}`, value: "" }];

    data.forEach(item => {
        const itemValue = item[key];
        const arr: string[] = Array.isArray(itemValue) ? itemValue : [];

        arr.forEach(val => {
            const isExist = values.some(v => v.value === val);
            if (!isExist) values.push({ label: capitalize(val), value: val });
        });
    });

    return values;
};

type FILTER_STATE = {
    search: string,
    property: string,
    group: string,
    doneBy: string,
    result: string,
    deliveryStatus: string,
    interaction: string
}

type TableDataType = UserData | PropertyData;
const initDeliveredState = {
    search: "",
    property: "",
    group: "",
    doneBy: "",
    result: "",
    deliveryStatus: "",
    interaction: ""
}

const PAGE_SIZE = 10;
const ReportList = ({ tab, tableState }: { tab: string, tableState: TABLE_STATE }) => {
    const [individualState, setIndividualState] = useState<FILTER_STATE>({ ...initDeliveredState });
    const [selectedData, setSelectedData] = useState<UserData | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const isMobile = useIsMobile();
    const isIndividualTab = tab === "individual";
    const { search, property, group, doneBy, interaction, result, deliveryStatus } = individualState;
    const tableColumn: ColumnsType<any> = isIndividualTab ? INDIVIDUAL_TABLE_COLUMNS : PROPERTY_TABLE_COLUMNS;

    const handleState = (selectKey: string, value: any) => {
        setIndividualState(prev => ({ ...prev, [selectKey]: value }));
    }

    const filteredData: UserData[] = useMemo(() => {
        return tableState.individualData.filter((item) => {
            const searchValue = search?.toLowerCase() || "";
            const name = `${item.first_name || ""} ${item.last_name || ""}`.toLowerCase();
            const email = item.email?.toLowerCase() || "";
    
            const matchesSearch =
                !searchValue ||
                name.includes(searchValue) ||
                email.includes(searchValue);
    
            // 🎯 Other filters (adjust based on your data shape)
            const matchesProperty = !property || item?.properties?.includes(property);
            const matchesGroup = !group || item?.groups?.includes(group);
            const matchesDoneBy = !doneBy || item.source === doneBy;
            const matchesInteraction = !interaction || item.interaction === interaction;
            const matchesResult = !result || item.result === result;
            const deliveryResult = !deliveryStatus || item.email_delivery === deliveryStatus;
    
            return (
                matchesSearch &&
                matchesProperty &&
                matchesGroup &&
                matchesDoneBy &&
                matchesInteraction &&
                matchesResult &&
                deliveryResult
            );
        });
    }, [search, property, group, doneBy, interaction, result, deliveryStatus, tableState.individualData]);

    const completeData: TableDataType[] = isIndividualTab ? filteredData : tableState.propertyData;

    const tableData: TableDataType[] = useMemo(() => {
        const startIdx = PAGE_SIZE * (currentPage - 1);
        return isMobile ? completeData.slice(0, startIdx + PAGE_SIZE) : completeData.slice(startIdx, startIdx + PAGE_SIZE);
    }, [currentPage, completeData, isMobile]);

    const { propertyOptions, groupOptions }: { propertyOptions: SelectOption[], groupOptions: SelectOption[] } = useMemo(() => {
        return {
            propertyOptions: getUniqueFormattedValues(tableState.individualData, "properties"),
            groupOptions: getUniqueFormattedValues(tableState.individualData, "groups"),
        }
    }, [tableState.individualData])

    useEffect(() => {
        if(tableState.isLoading) setCurrentPage(1);
    }, [tableState.isLoading])

    return <Spin spinning={tableState.isLoading}>
        <div className="report-section">
            <div className="table-header">
                <div className='description'>{isIndividualTab ? "Phishing test results: delivery, interactions, and outcomes" : "Performance metrics aggregated by property, including pass rate, engagement, and user activity across phishing simulations."}</div>
            </div>
            {isIndividualTab ? <div className={`campaign-filter-section`}>
                <DebounceInput placeholder="Search" setDebouncedSearch={(value) => handleState("search", value)} className="campaign-setup-search" />
                <SelectElement value={property} options={propertyOptions} onChange={(value) => handleState("property", value)} />
                <SelectElement value={group} options={groupOptions} onChange={(value) => handleState("group", value)} />
                <SelectElement value={result} options={resultOptions} onChange={(value) => handleState("result", value)} />
                <SelectElement value={doneBy} options={performedByOptions} onChange={(value) => handleState("doneBy", value)} />
                <SelectElement value={interaction} options={interactionOptions} onChange={(value) => handleState("interaction", value)} />
                <SelectElement value={deliveryStatus} options={DELIVERY_STATUS} onChange={(value) => handleState("deliveryStatus", value)} />
            </div> : <div className='cards-flex'>
                <PropertyCard title={`${tableState.propertyExtraData.averagePassRate}%`} desc='Average Pass Rate' iconType='trendingArrow' />
                <PropertyCard title={`${tableState.propertyExtraData.bestPerforming.name}`} desc='Best Performing' tagValue={tableState.propertyExtraData.bestPerforming.passRate} />
                <PropertyCard title={`${tableState.propertyExtraData.belowPassRate}`} desc='Below 90% Pass Rate' iconType='phishingManagerWarningModalPrefix' />
            </div>}

            {(!isMobile) ? <div className='table-section'>
                <Table
                    rowKey={(record) => record.key || record.id}
                    className="report-table"
                    dataSource={tableData}
                    columns={tableColumn}
                    pagination={false}
                    showSorterTooltip={false}
                    rowClassName={(_, index) =>
                        index % 2 === 0 ? "bg-white" : "bg-gray"
                    }
                    onRow={(record, rowIndex) => {
                        return {
                            onClick: (event) => {
                                if (isIndividualTab) setSelectedData(record);
                            },
                        };
                    }}
                />
                <PaginationElement
                    total={completeData.length}
                    pageSize={PAGE_SIZE}
                    current={currentPage}
                    onChange={setCurrentPage}
                />
            </div> : <div className='list-section'>
                {tableData.length === 0 ? <div className="cs-empty-state">
                    {tableState.isLoading ? "Loading..." : "No Data"}
                </div> : <InfinityScrollWrapper
                    dataLength={tableData.length}
                    hasMore={tableData.length < completeData.length}
                    onLoadMore={() => setTimeout(() => setCurrentPage((prev) => prev + 1), 500)}
                    height={600}
                    scrollableId="user-data-scroll-container"
                >
                    {tableData.map((item, idx) => isIndividualTab ?
                        <IndividualListItem userData={item as UserData} key={idx} /> :
                        <PropertyListItem propertyData={item as PropertyData} key={idx} />)}
                </InfinityScrollWrapper>}
            </div>}
        </div>
        {!!selectedData && <UserDetailDrawer data={selectedData} visible={!!selectedData} onClose={() => setSelectedData(null)} />}
    </Spin>
}

export default ReportList;


const SelectElement = ({ value, options, onChange }: { value: number | string, options: SelectOption[], onChange: (value: any) => void }) => {
    return <Select
        className="campaign-setup-select"
        dropdownClassName="campaign-setup-filter-option-select"
        suffixIcon={<div className="cs-dropdownWrapper">
            <Icon type="campaignSetupDropdown" />
        </div>}
        disabled={options.length <= 1}
        value={value}
        onChange={onChange}
        options={options}
        getPopupContainer={(trigger) => trigger.parentElement}
    />
}